import { select } from 'd3-selection';

import { renderConfig } from '../constants';
import { LabelPaddings } from './types';

// Compute padding values for label highlights, etc.
export function getLabelPaddings(formValues: SankeyFormValues): LabelPaddings {
  // First, how big are an em and an ex in the current font, roughly?
  // The height is the same for all characters.
  const canvasWidth = formValues.canvas_width ?? 0;
  const canvasHeight = formValues.canvas_height ?? 0;
  const fontSize = formValues.font_size ?? 14;
  const { width: emW, height: boundingBoxH } = measureText(
    canvasWidth,
    canvasHeight,
    fontSize,
    'm'
  );
  // The WIDTH of an 'x' is a crude estimate of the x-HEIGHT, but it's what we have for now.
  const { width: exH } = measureText(canvasWidth, canvasHeight, fontSize, 'x');
  const metrics = {
    // what fraction of the BoundingBox to lower labels to make them
    // vertically-centered relative to their Node
    dy: 0.29,
    // how many x-heights to pad above/below the BoundingBox
    top: 0.3,
    bot: 0.3,
    // how many em-widths to pad between the label and the highlight's edge
    // (could be on the left or right)
    inner: 0.35,
    // how many em-widths to pad at the end furthest from the Node
    outer: 0.38,
    // what multiple of 'inner' to move labels to the right
    marginRight: 1.35,
    // offset to add to marginRight when moving labels to left
    marginAdjLeft: 0,
  };
  const m = {
    dy: metrics.dy * boundingBoxH,
    top: metrics.top * exH,
    bot: metrics.bot * exH,
    inner: metrics.inner * emW,
    outer: metrics.outer * emW,
    lblMarginRight: 0,
    lblMarginLeft: 0,
  };
  // Compute the remaining values (which depend on values above).
  // lblMarginRight = total margin to give a label when it is to the right of a node.
  //                  (Note: this value basically includes m.inner)
  // lblMarginLeft = total margin when label is to the left
  m.lblMarginRight = metrics.marginRight * m.inner + 1;
  m.lblMarginLeft = (metrics.marginRight + metrics.marginAdjLeft) * m.inner + 1;
  return m;
}

export function measureText(
  canvasWidth: number,
  canvasHeight: number,
  fontSize: number,
  text: string
) {
  // To measure text sizes, first we make a dummy SVG area the user won't
  // see, with the same size and font details as the real diagram.
  // NOTE: d3.create('svg') on the fly without attaching it anywhere won't work unfortunately :(
  const scratchSVG = select('#svg_scratch')
    .attr('width', canvasWidth)
    .attr('height', canvasHeight)
    .attr('font-size', `${fontSize}px`)
    .attr('text-anchor', 'middle');
  // Clear out any past items
  scratchSVG.selectAll('*').remove();
  // Measure an SVG text element, placed at the hidden canvas' midpoint.
  const textNode = scratchSVG
    .append('text')
    .attr('x', canvasWidth / 2)
    .attr('y', canvasHeight / 2)
    .attr('font-weight', renderConfig.font.weight.bold)
    .text(text)
    .node();
  // Instead of failing on missing textNode, return a dummy bounding box.
  return textNode ? textNode.getBBox() : { width: 0, height: 0 };
}
